import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useProgress } from '../hooks/useProgress';
import { Nav } from '../components/Nav';
import { ProgressBar } from '../components/ProgressBar';
import { BookingModal } from '../components/BookingModal';
import { RescheduleModal } from '../components/RescheduleModal';
import { CallDayNudgeModal } from '../components/CallDayNudgeModal';
import { getBooking, getBmsForm, submitBmsForm, getCallDaySettings, updateVideoProgress } from '../utils/api';
import { C, CALL_DAY_WISTIA_ID, CALL_DAY_DURATION } from '../utils/constants';
import { formatClockTime } from '../utils/formatTime';
import { trackCourseVideo } from '../utils/courseVideoTracker';

// (No localStorage persistence — the nudge intentionally fires on EVERY
// visit to /call-day for users who haven't completed all 6 course days,
// and never fires once the course is complete.)

const GOLD = '#F5A623';
const DARK_GOLD = '#E8830A';

const useCountdown = (sessionDate) => {
  const calc = useCallback(() => {
    if (!sessionDate) return null;
    const diff = new Date(sessionDate) - new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, past: true, withinFive: false };
    return {
      days:       Math.floor(diff / 86400000),
      hours:      Math.floor((diff % 86400000) / 3600000),
      minutes:    Math.floor((diff % 3600000) / 60000),
      seconds:    Math.floor((diff % 60000) / 1000),
      past:       false,
      withinFive: diff < 5 * 60 * 1000,
    };
  }, [sessionDate]);

  const [countdown, setCountdown] = useState(calc);
  useEffect(() => {
    if (!sessionDate) return;
    setCountdown(calc());
    const id = setInterval(() => setCountdown(calc()), 1000);
    return () => clearInterval(id);
  }, [sessionDate, calc]);
  return countdown;
};

// ── Score input (0–5 radio row) ───────────────────────────────────────────────

const ScoreInput = ({ value, onChange, lowLabel, highLabel }) => (
  <div>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {[0, 1, 2, 3, 4, 5].map(n => (
        <label key={n} style={{ cursor: 'pointer' }}>
          <input
            type="radio"
            name={Math.random()} // unique per instance
            checked={value === n}
            onChange={() => onChange(n)}
            style={{ display: 'none' }}
          />
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.95rem',
            fontWeight: 700,
            border: value === n ? `2px solid ${GOLD}` : `2px solid #E8D5C4`,
            background: value === n ? GOLD : '#FDFAF8',
            color: value === n ? '#3D1F02' : '#AD795B',
            transition: 'all 0.12s',
            fontFamily: "'Forum', serif",
          }}>
            {n}
          </div>
        </label>
      ))}
    </div>
    {(lowLabel || highLabel) && (
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: '0.72rem', color: '#AD795B' }}>
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    )}
  </div>
);

// ── Multi-select checkboxes ───────────────────────────────────────────────────

const MultiCheck = ({ options, value = [], onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {options.map(opt => {
      const checked = value.includes(opt);
      return (
        <label key={opt} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <div style={{
            flexShrink: 0,
            width: 20, height: 20,
            borderRadius: 5,
            border: checked ? `2px solid ${GOLD}` : '2px solid #E8D5C4',
            background: checked ? GOLD : '#FDFAF8',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginTop: 2,
            transition: 'all 0.12s',
          }}>
            {checked && <span style={{ color: '#3D1F02', fontSize: '0.7rem', fontWeight: 900 }}>✓</span>}
          </div>
          <input
            type="checkbox"
            style={{ display: 'none' }}
            checked={checked}
            onChange={() => {
              const next = checked ? value.filter(v => v !== opt) : [...value, opt];
              onChange(next);
            }}
          />
          <span style={{ fontSize: '0.88rem', color: '#3D1F02', lineHeight: 1.5 }}>{opt}</span>
        </label>
      );
    })}
  </div>
);

// ── Single-select radio ───────────────────────────────────────────────────────

const RadioSelect = ({ options, value, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {options.map(opt => {
      const checked = value === opt;
      return (
        <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <div style={{
            flexShrink: 0,
            width: 20, height: 20,
            borderRadius: '50%',
            border: checked ? `2px solid ${GOLD}` : '2px solid #E8D5C4',
            background: checked ? GOLD : '#FDFAF8',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.12s',
          }}>
            {checked && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3D1F02' }} />}
          </div>
          <span style={{ fontSize: '0.88rem', color: '#3D1F02', lineHeight: 1.5 }}>{opt}</span>
        </label>
      );
    })}
  </div>
);

// ── Section heading ───────────────────────────────────────────────────────────

const SectionHeading = ({ num, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
    <div style={{
      width: 32, height: 32, borderRadius: 8,
      background: `linear-gradient(135deg, ${DARK_GOLD}, ${GOLD})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Forum', serif", fontSize: '0.85rem', fontWeight: 700, color: '#3D1F02',
      flexShrink: 0,
    }}>
      {num}
    </div>
    <div style={{ fontFamily: "'Forum', serif", fontSize: '1rem', color: C.brown, letterSpacing: '0.03em' }}>
      {title}
    </div>
  </div>
);

// ── Question block ────────────────────────────────────────────────────────────

const Question = ({ q, optional, children }) => (
  <div style={{ marginBottom: 24 }}>
    <p style={{
      fontSize: '0.9rem',
      color: C.brown,
      lineHeight: 1.65,
      marginBottom: 12,
      fontStyle: 'italic',
    }}>
      {q}{optional && <span style={{ color: '#AD795B', fontStyle: 'normal', fontSize: '0.78rem', marginLeft: 8 }}>(optional)</span>}
    </p>
    {children}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const WEEK_OPTIONS = [
  "It opened my eyes to things I hadn't seen before",
  "It confirmed things I already sensed but couldn't name",
  "It was uncomfortable in ways I'm still sitting with",
  "It felt a bit surface level for where I am",
  "A mix of all of the above",
];

const LIFE_OPTIONS = [
  "Relationships (romantic, family, friendships)",
  "Work / career / business",
  "Self-worth and how I feel about myself day to day",
  "Taking action and making decisions",
  "All of the above",
];

const DURATION_OPTIONS = [
  "Less than a year",
  "1–3 years",
  "3–5 years",
  "Most of my life",
];

const EMPTY_FORM = {
  week_landing:               [],
  biggest_realisation:        '',
  inner_ownership_score:      null,
  emotional_legitimacy_score: null,
  aligned_action_score:       null,
  main_loop:                  '',
  belief_1:                   '',
  belief_2:                   '',
  belief_3:                   '',
  pattern_duration:           '',
  successful_session:         '',
  life_area_impacted:         [],
  support_score:              null,
  readiness_score:            null,
  anything_else:              '',
};

export const CallDay = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { completedDays, isDayComplete, getDayProgress, refetch: refetchProgress, loading: progressLoading } = useProgress(); // eslint-disable-line no-unused-vars

  const [booking,      setBooking]      = useState(null);
  const [bookingReady, setBookingReady] = useState(false);
  const [videoUrl,     setVideoUrl]     = useState(null);
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [submitted,    setSubmitted]    = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [submitError,  setSubmitError]  = useState('');
  const [videoWatched, setVideoWatched] = useState(false); // just-clicked state; reload-persistence comes from getDayProgress(7)
  const [savingWatch,  setSavingWatch]  = useState(false);

  // BookingModal state — the not-booked branch (Part 1F) and the post-close
  // refetch+polling pattern mirror Dashboard.jsx:99–126 exactly so a booking
  // made on this page correctly flips the page out of the not-booked state.
  const [showBooking,  setShowBooking]  = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);

  // Soft-nudge modal state — fires every time an unfinished-course user
  // lands on /call-day, never fires once all 6 days are complete. No
  // localStorage: dismissing only closes the modal for the current visit,
  // the next /call-day visit re-shows it (until the course is finished).
  //
  // Start false; flip to true once useProgress resolves (otherwise the
  // initial render — when progress is still empty and completedDays is 0
  // — would flash the nudge to users who've actually completed the course).
  const [showNudge,    setShowNudge]    = useState(false);

  const countdown = useCountdown(booking?.sessionDate);

  // Standalone booking fetcher so both the on-mount effect and the modal
  // close handler can call it. Mirrors Dashboard.jsx:99–104.
  const fetchBooking = useCallback(async () => {
    try {
      const res = await getBooking();
      setBooking(res.data);
      setBookingReady(true);
    } catch (e) { /* not critical */ }
  }, []);

  // Initial load — booking via fetchBooking, plus the form + video settings
  // in parallel. These three are independent and don't share state.
  useEffect(() => {
    fetchBooking();
    Promise.all([
      getBmsForm().then(r => r.data).catch(() => null),
      getCallDaySettings().then(r => r.data).catch(() => null),
    ]).then(([formData, settings]) => {
      if (settings?.callDayVideoUrl) setVideoUrl(settings.callDayVideoUrl);
      if (formData?.submitted_at) {
        setSubmitted(true);
        setForm({ ...EMPTY_FORM, ...formData });
      }
    });
  }, [fetchBooking]);

  // Poll for booking if not yet confirmed (hedge against Booking Hub webhook
  // delay — the awaited webhook fix makes the race much less likely, but
  // Render cold-starts can still cause a brief lag). Mirrors
  // Dashboard.jsx:109–119.
  useEffect(() => {
    if (booking?.callBooked) return;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      const res = await getBooking().catch(() => null);
      if (res?.data?.callBooked) { setBooking(res.data); clearInterval(interval); }
      if (attempts >= 10) clearInterval(interval);
    }, 4000);
    return () => clearInterval(interval);
  }, [booking?.callBooked]);

  const handleBookingModalClose = () => {
    setShowBooking(false);
    // Re-check booking status after modal closes in case Booking Hub webhook already fired
    setTimeout(fetchBooking, 1500);
  };

  // Decide whether to show the nudge — runs once per /call-day mount, AFTER
  // useProgress has finished loading. If the user hasn't completed all 6
  // days, the modal opens; once dismissed (close button / overlay click /
  // "Go to Day X"), it stays closed for this visit. Next visit re-runs
  // this effect and re-shows it until completedDays >= 6.
  useEffect(() => {
    if (progressLoading) return;
    if (completedDays < 6) setShowNudge(true);
    // Intentionally only watch the loading flag — completedDays is captured
    // at the moment progress finishes loading and shouldn't re-fire the
    // nudge if it changes mid-visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressLoading]);

  const handleNudgeClose = () => {
    setShowNudge(false);
  };

  // Used by the nudge "Go to Day X" button.
  const nextIncompleteDay = [1, 2, 3, 4, 5, 6].find(n => !isDayComplete(n)) ?? 6;

  const field = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      await submitBmsForm(form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setSubmitError(e?.response?.data?.error || 'Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Mark the call-day video watched. Mirrors the days 1–6 model exactly
  // (DayVideo.jsx handleContinue): optimistic flip → updateVideoProgress(dayNum, 100)
  // → refetch. dayNum 7 upserts the first-ever day-7 ry_progress row with
  // video_complete = true. No day_complete / tag / /complete — video only.
  // Record how the client watches this video (utils/courseVideoTracker.js).
  // Recording only — never affects playback or whether the day counts as done.
  useEffect(() => {
    if (!CALL_DAY_WISTIA_ID) return undefined;
    return trackCourseVideo(CALL_DAY_WISTIA_ID, 7);
  }, [CALL_DAY_WISTIA_ID]);

  // Wistia embed script injection (idempotent) — identical to DayVideo.jsx.
  useEffect(() => {
    if (!CALL_DAY_WISTIA_ID) return;
    if (!document.querySelector('script[src="https://fast.wistia.com/assets/external/E-v1.js"]')) {
      const s = document.createElement('script');
      s.src = 'https://fast.wistia.com/assets/external/E-v1.js';
      s.async = true;
      document.head.appendChild(s);
    }
    const mediaSrc = `https://fast.wistia.com/embed/medias/${CALL_DAY_WISTIA_ID}.jsonp`;
    if (!document.querySelector(`script[src="${mediaSrc}"]`)) {
      const m = document.createElement('script');
      m.src = mediaSrc;
      m.async = true;
      document.head.appendChild(m);
    }
  }, []);

  const handleMarkWatched = async () => {
    if (savingWatch) return; // re-entrancy guard
    setSavingWatch(true);
    setVideoWatched(true);
    await updateVideoProgress(7, 100).catch(() => {});
    await refetchProgress();
    setSavingWatch(false);
  };

  const handleAdmin = () => navigate('/admin');

  // ── Locking logic ──────────────────────────────────────────────────────────
  if (!bookingReady) {
    return (
      <div style={{ minHeight: '100vh', background: C.cream }}>
        <Nav user={user} onAdmin={handleAdmin} />
        <ProgressBar completedDays={completedDays} />
        <div style={{ textAlign: 'center', padding: '80px 24px', color: C.terracotta, fontFamily: "'Forum', serif" }}>
          Loading…
        </div>
      </div>
    );
  }

  const sessionMs = booking?.sessionDate ? new Date(booking.sessionDate).getTime() : null;
  const diffMs    = sessionMs ? sessionMs - Date.now() : null;

  // Not booked at all — soften the language, surface the booking modal inline
  // (Part 1F). No more bouncing back to the Dashboard; the user can book
  // directly from here.
  if (!booking?.callBooked || sessionMs === null) {
    return (
      <div style={{ minHeight: '100vh', background: C.cream }}>
        <Nav user={user} onAdmin={handleAdmin} />
        <ProgressBar completedDays={completedDays} />
        <div style={{ maxWidth: 600, margin: '0 auto', padding: '60px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>🔒</div>
          <h2 style={{ fontFamily: "'Forum', serif", color: C.brown, marginBottom: 12 }}>
            Book your session to unlock
          </h2>
          <p style={{ color: C.terracotta, fontSize: '0.9rem', marginBottom: 24 }}>
            Your Breakthrough Mapping Session unlocks this page once it's booked.
          </p>
          <button onClick={() => setShowBooking(true)} className="btn-primary">
            Book Session →
          </button>
        </div>
        {showBooking && (
          <BookingModal onClose={handleBookingModalClose} daysCompleted={completedDays} />
        )}
        {showReschedule && booking?.appointmentId && (
          <RescheduleModal
            booking={booking}
            onClose={() => setShowReschedule(false)}
            onRescheduled={fetchBooking}
          />
        )}
      </div>
    );
  }

  // (Removed: the "Booked but locked / Coming Soon / This unlocks 24 hours
  // before your session" branch. Post-booking the user goes straight to the
  // full page below.)

  // ── Full page (unlocked) ───────────────────────────────────────────────────
  const sessionDateFormatted = booking.sessionDate
    ? new Date(booking.sessionDate).toLocaleDateString('en-GB', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
      }).replace(/\b\d{1,2}:\d{2}\b/, formatClockTime(new Date(booking.sessionDate)))
    : null;

  return (
    <div style={{ minHeight: '100vh', background: C.cream }}>
      <Nav user={user} onAdmin={handleAdmin} />
      <ProgressBar completedDays={completedDays} />

      <style>{`
        @keyframes zoomPulse {
          0%,100% { box-shadow: 0 4px 24px rgba(45,140,255,0.5); transform: scale(1); }
          50%      { box-shadow: 0 8px 36px rgba(45,140,255,0.85); transform: scale(1.025); }
        }
        @keyframes goldPulse {
          0%,100% { box-shadow: 0 4px 20px rgba(245,166,35,0.4); }
          50%      { box-shadow: 0 6px 28px rgba(245,166,35,0.75); }
        }
      `}</style>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 16px 80px' }}>

        {/* Breadcrumb */}
        <nav style={{ fontSize: '0.8rem', color: C.terracotta, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Link to="/dashboard" style={{ color: C.terracotta, textDecoration: 'none' }}>Dashboard</Link>
          <span style={{ opacity: 0.5 }}>›</span>
          <span style={{ color: C.brown, fontWeight: 600 }}>Call Day</span>
        </nav>

        {/* Header */}
        <div className="animate-in" style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: "'Forum', serif", fontSize: '0.7rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: GOLD, marginBottom: 6 }}>
            Your 1:1 Session
          </div>
          <h1 style={{ fontFamily: "'Forum', serif", color: C.brown, fontSize: 'clamp(1.4rem,4vw,1.9rem)', letterSpacing: '0.02em', marginBottom: 4 }}>
            Call Day
          </h1>
          {CALL_DAY_DURATION && (
            <p style={{ color: C.terracotta, fontSize: '0.88rem', opacity: 0.75, marginBottom: 4 }}>
              Video length - {CALL_DAY_DURATION}
            </p>
          )}
          {booking?.zoomLink && (
            <a
              href={booking.zoomLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                marginTop: 12,
                marginBottom: 4,
                fontFamily: "'Forum', serif",
                background: 'linear-gradient(135deg, #2D8CFF, #1a6fd4)',
                color: '#fff',
                padding: '14px 32px',
                borderRadius: 8,
                fontSize: '1rem',
                fontWeight: 700,
                textDecoration: 'none',
                boxShadow: '0 4px 20px rgba(45,140,255,0.5)',
              }}
            >
              Join on Zoom →
            </a>
          )}
          <p style={{ color: C.terracotta, fontSize: '0.88rem' }}>
            Watch the prep video, complete your form, and join when it's time.
          </p>
          {/* Quiet reschedule link — only while booked, with an appointment id,
              and before the session has passed. Opens the native in-portal
              RescheduleModal (was a link to the booking-hub manage page). */}
          {booking?.appointmentId && !countdown?.past && (
            <button
              type="button"
              onClick={() => setShowReschedule(true)}
              style={{
                display: 'inline-block',
                marginTop: 10,
                background: 'none',
                border: 'none',
                padding: 0,
                color: C.terracotta,
                fontSize: '0.8rem',
                fontFamily: "'Nunito', sans-serif",
                textDecoration: 'underline',
                opacity: 0.85,
                cursor: 'pointer',
              }}
            >
              Need to reschedule?
            </button>
          )}
        </div>

        {/* ── Small countdown chip (replaces the big banner block) ── */}
        {/* Part 1C: quiet status indicator, not the main event. The big
            countdown tiles are gone; seconds are dropped at this size. The
            session date sits on a second line so it doesn't dominate. */}
        {countdown?.past ? (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            background: 'rgba(107,62,10,0.08)',
            border: `1px solid ${C.border}`,
            borderRadius: 999,
            padding: '8px 16px',
            marginBottom: 28,
            fontFamily: "'Forum', serif",
            color: C.brown,
            fontSize: '0.85rem',
            letterSpacing: '0.04em',
          }}>
            <span style={{ color: GOLD }}>✓</span> Session complete
          </div>
        ) : (
          <div style={{ marginBottom: 28 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              background: 'rgba(107,62,10,0.08)',
              border: `1px solid ${C.border}`,
              borderRadius: 999,
              padding: '8px 16px',
              fontFamily: "'Forum', serif",
              color: C.brown,
              fontSize: '0.85rem',
              letterSpacing: '0.04em',
            }}>
              <span style={{ color: GOLD, fontWeight: 700 }}>✓ Session booked</span>
              {countdown && (
                <>
                  <span style={{ opacity: 0.4 }}>·</span>
                  <span>
                    {countdown.days}d {String(countdown.hours).padStart(2, '0')}h {String(countdown.minutes).padStart(2, '0')}m to go
                  </span>
                </>
              )}
            </div>
            {sessionDateFormatted && (
              <div style={{
                color: C.terracotta,
                fontSize: '0.78rem',
                marginTop: 8,
                fontFamily: "'Nunito', sans-serif",
              }}>
                {sessionDateFormatted}
              </div>
            )}
          </div>
        )}

        {/* ── Prep video (moved up — Part 1B) ── */}
        <section style={{ marginBottom: 28 }}>
          <h2 style={{ fontFamily: "'Forum', serif", color: C.brown, fontSize: '1.1rem', letterSpacing: '0.03em', marginBottom: 6 }}>
            Watch Before Your Session
          </h2>
          <p style={{ color: C.terracotta, fontSize: '0.82rem', marginBottom: 16 }}>
            A short video to help you get the most out of our time together.
          </p>
          <div style={{
            borderRadius: 16, overflow: 'hidden', aspectRatio: '16/9',
            boxShadow: '0 8px 40px rgba(0,0,0,0.15)', background: '#1a0800', position: 'relative',
          }}>
            {CALL_DAY_WISTIA_ID ? (
              // Same Wistia embed as days 1–6 (DayVideo.jsx): the async
              // E-v1.js player fills this div. Scripts injected in the effect
              // above.
              <div
                className={`wistia_embed wistia_async_${CALL_DAY_WISTIA_ID}`}
                style={{ width: '100%', height: '100%', position: 'relative' }}
              >
                &nbsp;
              </div>
            ) : (
              <div style={{
                width: '100%', height: '100%',
                background: `linear-gradient(135deg, #1a0800 0%, #2d1204 50%, #1a0800 100%)`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
              }}>
                <div style={{
                  width: 80, height: 80, borderRadius: '50%',
                  background: 'rgba(245,166,35,0.15)', border: '2px solid rgba(245,166,35,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem',
                }}>
                  🎬
                </div>
                <p style={{ fontFamily: "'Forum', serif", color: GOLD, fontSize: '1rem', letterSpacing: '0.06em' }}>
                  Prep Video Coming Soon
                </p>
              </div>
            )}
          </div>

          {/* Click-to-complete: mirrors days 1–6 (a click marks video_complete).
              Writes the first-ever day-7 ry_progress row via updateVideoProgress(7,100). */}
          {CALL_DAY_WISTIA_ID && (
            videoWatched || getDayProgress(7)?.video_complete === true ? (
              <div style={{
                marginTop: 16, width: '100%', textAlign: 'center',
                padding: '13px 20px', borderRadius: 8,
                background: '#F0FAF5', border: '1.5px solid #2E7D5E',
                color: '#2E7D5E', fontWeight: 700, fontSize: '0.95rem',
              }}>
                ✓ Call-day video watched
              </div>
            ) : (
              <button
                className="btn-primary"
                onClick={handleMarkWatched}
                disabled={savingWatch}
                style={{ width: '100%', marginTop: 16 }}
              >
                {savingWatch ? 'Saving…' : "I've watched the call-day video"}
              </button>
            )
          )}
        </section>

        {/* ── Pre-call form ── */}
        <section style={{ background: '#fff', borderRadius: 14, padding: '28px 28px', border: `1.5px solid ${C.border}` }}>

          <div style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: "'Forum', serif", color: GOLD, fontSize: '0.68rem', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 6 }}>
              Pre-Call Prep Form
            </div>
            <h2 style={{ fontFamily: "'Forum', serif", color: C.brown, fontSize: '1.2rem', marginBottom: 6 }}>
              Help Me Show Up Fully Prepared For Our Call Together
            </h2>
            <p style={{ color: C.terracotta, fontSize: '0.84rem', lineHeight: 1.65 }}>
              Please complete this before our call. The more you can give me now, the more I can help you through it.
            </p>
          </div>

          {/* Success state */}
          {submitted ? (
            <div style={{
              background: '#F0FAF5', border: '1.5px solid #2E7D5E',
              borderRadius: 12, padding: '24px 28px', textAlign: 'center',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: 12 }}>✅</div>
              <div style={{ fontFamily: "'Forum', serif", color: '#2E7D5E', fontSize: '1.1rem', marginBottom: 8 }}>
                You're all set
              </div>
              <p style={{ color: '#3D7A60', fontSize: '0.9rem', lineHeight: 1.65 }}>
                I'll read this before our call. See you soon.
              </p>
            </div>
          ) : (
            <>
              {/* ── SECTION 1 — YOUR WEEK ── */}
              <div style={{ paddingBottom: 28, marginBottom: 28, borderBottom: `1px solid ${C.border}` }}>
                <SectionHeading num="1" title="Your Week" />

                <Question q="How did the week land overall?">
                  <MultiCheck
                    options={WEEK_OPTIONS}
                    value={form.week_landing}
                    onChange={v => field('week_landing', v)}
                  />
                </Question>

                <Question q="What was the single biggest realisation or shift for you this week?">
                  <textarea
                    value={form.biggest_realisation}
                    onChange={e => field('biggest_realisation', e.target.value)}
                    placeholder="Write freely here..."
                    rows={4}
                  />
                </Question>
              </div>

              {/* ── SECTION 2 — YOUR THREE PILLARS ── */}
              <div style={{ paddingBottom: 28, marginBottom: 28, borderBottom: `1px solid ${C.border}` }}>
                <SectionHeading num="2" title="Your Three Pillars" />
                <p style={{ fontSize: '0.84rem', color: C.terracotta, marginBottom: 20, lineHeight: 1.6, fontStyle: 'italic' }}>
                  Rate how eroded each pillar feels for you right now. 0 = completely eroded / 5 = feeling strong
                </p>

                <Question q="Inner Ownership — how much you trust your own perspective, opinions, values and beliefs">
                  <ScoreInput
                    value={form.inner_ownership_score}
                    onChange={v => field('inner_ownership_score', v)}
                    lowLabel="0 = completely eroded"
                    highLabel="5 = feeling strong"
                  />
                </Question>

                <Question q="Emotional Legitimacy — how safe you feel to feel and express your emotions without judging or overriding them">
                  <ScoreInput
                    value={form.emotional_legitimacy_score}
                    onChange={v => field('emotional_legitimacy_score', v)}
                    lowLabel="0 = completely eroded"
                    highLabel="5 = feeling strong"
                  />
                </Question>

                <Question q="Aligned Action — how often your actions actually reflect what feels true for you, rather than what feels safe">
                  <ScoreInput
                    value={form.aligned_action_score}
                    onChange={v => field('aligned_action_score', v)}
                    lowLabel="0 = completely eroded"
                    highLabel="5 = feeling strong"
                  />
                </Question>
              </div>

              {/* ── SECTION 3 — YOUR LOOPS & BELIEFS ── */}
              <div style={{ paddingBottom: 28, marginBottom: 28, borderBottom: `1px solid ${C.border}` }}>
                <SectionHeading num="3" title="Your Loops & Beliefs" />

                <Question q="Describe the loop, belief, emotion, trigger or action you most want to work on today. Just give me a picture of the pattern — it doesn't need to be perfectly mapped out.">
                  <textarea
                    value={form.main_loop}
                    onChange={e => field('main_loop', e.target.value)}
                    placeholder="Write freely here..."
                    rows={4}
                  />
                </Question>

                <div style={{ marginBottom: 24 }}>
                  <p style={{ fontSize: '0.9rem', color: C.brown, lineHeight: 1.65, marginBottom: 4, fontStyle: 'italic' }}>
                    What are the top 1–3 beliefs you feel are most keeping you stuck right now?
                  </p>
                  <p style={{ fontSize: '0.8rem', color: C.terracotta, marginBottom: 12, lineHeight: 1.6 }}>
                    For example: "I'm too much," "If I say no, people will leave," "I need to have it all together before I can ask for help"
                  </p>
                  {[['belief_1', '1'], ['belief_2', '2'], ['belief_3', '3']].map(([key, num]) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <div style={{
                        flexShrink: 0, width: 28, height: 28, borderRadius: 6,
                        background: `linear-gradient(135deg, ${DARK_GOLD}, ${GOLD})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.78rem', fontWeight: 800, color: '#3D1F02',
                        fontFamily: "'Forum', serif",
                      }}>
                        {num}
                      </div>
                      <input
                        type="text"
                        value={form[key]}
                        onChange={e => field(key, e.target.value)}
                        placeholder={`Belief ${num}...`}
                        style={{
                          flex: 1, padding: '10px 12px', fontSize: '0.88rem',
                          border: `1.5px solid ${C.border}`, borderRadius: 8,
                          fontFamily: "'Nunito', sans-serif", color: C.brown,
                          background: '#FDFAF8', outline: 'none',
                        }}
                      />
                    </div>
                  ))}
                </div>

                <Question q="How long have these patterns been running?">
                  <RadioSelect
                    options={DURATION_OPTIONS}
                    value={form.pattern_duration}
                    onChange={v => field('pattern_duration', v)}
                  />
                </Question>
              </div>

              {/* ── SECTION 4 — WHERE YOU WANT TO GO ── */}
              <div style={{ paddingBottom: 8, marginBottom: 0 }}>
                <SectionHeading num="4" title="Where You Want to Go" />

                <Question q="What would a successful Breakthrough Mapping Session look like for you today? What do you want to walk away feeling, knowing or having seen more clearly?">
                  <textarea
                    value={form.successful_session}
                    onChange={e => field('successful_session', e.target.value)}
                    placeholder="Write freely here..."
                    rows={4}
                  />
                </Question>

                <Question q="What area of your life feels most impacted by your conditioning right now?">
                  <MultiCheck
                    options={LIFE_OPTIONS}
                    value={form.life_area_impacted}
                    onChange={v => field('life_area_impacted', v)}
                  />
                </Question>

                <Question q="How supported do you feel in this work by the people around you right now?">
                  <ScoreInput
                    value={form.support_score}
                    onChange={v => field('support_score', v)}
                    lowLabel="0 = not at all"
                    highLabel="5 = very supported"
                  />
                </Question>

                <Question q="How ready do you feel to go deeper into this work beyond the course?">
                  <ScoreInput
                    value={form.readiness_score}
                    onChange={v => field('readiness_score', v)}
                    lowLabel="0 = not ready yet"
                    highLabel="5 = completely ready"
                  />
                </Question>

                <Question q="Is there anything else you want me to know before our session today?" optional>
                  <textarea
                    value={form.anything_else}
                    onChange={e => field('anything_else', e.target.value)}
                    placeholder="Anything at all — no detail is too small..."
                    rows={3}
                  />
                </Question>
              </div>

              {/* Submit error */}
              {submitError && (
                <div style={{ marginBottom: 12, padding: '10px 14px', background: '#FEF2F0', border: '1px solid #FECACA', borderRadius: 8, fontSize: '0.85rem', color: '#C0392B' }}>
                  ✕ {submitError}
                </div>
              )}

              {/* Submit button */}
              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  width: '100%',
                  marginTop: 28,
                  background: submitting ? C.border : `linear-gradient(135deg, ${DARK_GOLD}, ${GOLD})`,
                  color: submitting ? '#fff' : '#3D1F02',
                  fontSize: '1rem',
                  animation: !submitting ? 'goldPulse 2.5s ease-in-out infinite' : 'none',
                }}
              >
                {submitting ? 'Submitting…' : "Submit your form — And I'll see you soon!"}
              </button>
            </>
          )}
        </section>

        {/* ── Join Zoom (relocated AFTER the form — Part 1D) ── */}
        {/* Behaviour preserved verbatim from the old banner block: gold/blue
            gradient, pulses at ≤5 min via goldPulse/zoomPulse keyframes
            defined above, opens booking.zoomLink in a new tab. Hidden in the
            past-session state. */}
        {!countdown?.past && (
          <section style={{ marginTop: 28, textAlign: 'center' }}>
            {booking?.zoomLink ? (
              <a
                href={booking.zoomLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  fontFamily: "'Forum', serif",
                  background: `linear-gradient(135deg, #2D8CFF, #1a6fd4)`,
                  color: '#fff',
                  padding: '14px 32px',
                  borderRadius: 8,
                  fontSize: '1rem',
                  fontWeight: 700,
                  textDecoration: 'none',
                  boxShadow: '0 4px 20px rgba(45,140,255,0.5)',
                  animation: countdown?.withinFive ? 'zoomPulse 1.5s ease-in-out infinite' : 'none',
                }}
              >
                Join Your Breakthrough Mapping Session →
              </a>
            ) : (
              <p style={{ color: C.terracotta, fontSize: '0.85rem' }}>
                Your Zoom link will arrive by email shortly before our session. 🎯
              </p>
            )}
          </section>
        )}

      </div>

      {/* Soft-nudge dialog — first-time-on-this-device only, only for users
          who haven't finished all 6 days. Wired per Part 1E. */}
      {showNudge && (
        <CallDayNudgeModal
          nextDay={nextIncompleteDay}
          onClose={handleNudgeClose}
          onGoToDay={() => navigate(`/day/${nextIncompleteDay}/video`)}
        />
      )}
    </div>
  );
};
